/**
 * AI CONTEXT
 * Purpose: centralized visual theme tokens (colors, attendance palette, room palette).
 * Exposes: typed ThemeContext values consumed across UI.
 * Used by: nearly all styled UI components and Antd theme adapter.
 * Importance: single source of truth for app look-and-feel.
 * Refactor: split giant defaultValue into themed token modules (core, attendance, rooms).
 */
import { type MenuTheme } from "antd"
import { createContext, type ReactNode, useContext } from "react"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type AttendanceStatus } from "#customTypes/types"
import { COLORS } from "#styles/colors"

export type Hexcode = string

export interface ThemeContextType {
  theme: MenuTheme
  mainColor: Hexcode
  activeColor: Hexcode
  backgroundColor: Hexcode
  accentColor: Hexcode
  secondaryColor: Hexcode
  highlightColor: Hexcode
  altColor: Hexcode

  white: Hexcode
  black: Hexcode
  disabled: Hexcode

  success: Hexcode
  warning: Hexcode
  error: Hexcode

  eric: Record<"green" | "red" | "blue" | "gray", Hexcode>

  christy: Record<
    | "tryoutOrange"
    | "tryoutOrangeDark"
    | "makeupGreen"
    | "totalStudents"
    | "tryouts"
    | "makeups"
    | "plannedAbsences",
    Hexcode
  >

  header: Hexcode
  border: Hexcode
  body1: Hexcode
  body2: Hexcode
  body3: Hexcode
  body4: Hexcode
  body5: Hexcode

  gray: Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8, Hexcode>

  schedule: Hexcode

  highlight: Hexcode
  highlightGreen: Hexcode
  highlightYellow: Hexcode
  highlightRed: Hexcode

  redButton: Hexcode
  greenButton: Hexcode

  sessionSelection: {
    borderColor: Hexcode
    selectedBorderColor: Hexcode
    deselectedBorderColor: Hexcode
    headerBackgroundColor: Hexcode
  }

  attendance: Record<AttendanceStatus, Hexcode>

  iconColors: Record<
    | "students"
    | "tryouts"
    | "makeups"
    | "plannedAbsences"
    | "assistants"
    | "exceptions"
    | "empty",
    Hexcode
  >

  newPalette: Record<
    | "yellowRoom"
    | "yellowRoomDark"
    | "greenRoom"
    | "greenRoomDark"
    | "pinkRoom"
    | "pinkRoomDark"
    | "orangeRoom"
    | "orangeRoomDark"
    | "purpleRoom"
    | "purpleRoomDark"
    | "blueRoom"
    | "blueRoomDark"
    | "portfolioRoom"
    | "portfolioRoomDark",
    Hexcode
  >

  colors: typeof COLORS
}

const defaultValue: ThemeContextType = {
  theme: "light",

  // Placeholder theme
  mainColor: "#EDAF68", // Header color
  activeColor: "#d68b36",
  backgroundColor: "#FFDFB5",
  highlightColor: "#FFE8CB",
  // highlightColor: "#EE3C7E",
  // hoverColor: "#F7A3A3",
  // highlightColor: "#CA3F72",
  header: "#EEE5D1",
  highlight: "#FFE8CB",
  body1: "#F6EFE9",
  body2: "#f3f2ea",
  body3: "#FBF8F6",
  body4: "#F6F8F3",
  body5: "#F7F7F7",
  schedule: "#F9F8F5",

  gray: {
    1: "#F2F2F2",
    2: "#E2E2E2",
    3: "#D2D2D2",
    4: "#C2C2C2",
    5: "#B2B2B2",
    6: "#A2A2A2",
    7: "#929292",
    8: "#828282",
  },

  // backgroundColor: "#F9F8F5",
  accentColor: "",
  secondaryColor: "#35CE8D",
  // highlightColor: "#FFE9CD",
  altColor: "#3F84E5",

  white: "#fefefc", // Slightly warm white
  black: "#141400", // Slightly warm black
  disabled: "#CCC",

  border: "#BBBBBB33",
  highlightGreen: "#DDE68B",
  highlightYellow: "#E8CB65",
  highlightRed: "#E4A18F",

  redButton: "#F4D6CF",
  greenButton: "#DAE190",

  success: "#1D8921",
  warning: "#D1A236",
  error: "#D44040",

  sessionSelection: {
    borderColor: "#E2E2E2", // gray[2]
    selectedBorderColor: "#35CE8D",
    deselectedBorderColor: "#ce3535",
    headerBackgroundColor: "#620e0e",
  },

  iconColors: {
    students: "#448434", // was #53a140 (now AA w/ white)
    assistants: "#448434", // was #53a140 (now AA w/ white)
    tryouts: "#c6009e", // already AA
    makeups: "#007E8A", // was #12a3bc
    plannedAbsences: "#F18E00", // was #F18E00
    exceptions: "#C4452B", // amber-red, AA on white
    empty: "#9A8A9A",
  },

  eric: {
    green: "#1D8921",
    red: "#D44040",
    blue: "#2db7f5",
    gray: "#888",
  },

  christy: {
    tryoutOrange: "#E8764A",
    tryoutOrangeDark: "#EC9F05",
    makeupGreen: "#A7B936",
    totalStudents: "#ABABAB",
    tryouts: "#EA916E",
    makeups: "#C5C96A",
    plannedAbsences: "#D7D7D7",
  },

  newPalette: {
    yellowRoom: "#FDE047",
    yellowRoomDark: "#4A3A00",
    greenRoom: "#BEF264",
    greenRoomDark: "#174A1F",
    pinkRoom: "#F9A8D4",
    pinkRoomDark: "#5A2B28",
    orangeRoom: "#FDBA74",
    orangeRoomDark: "#503316",
    purpleRoom: "#C4B5FD",
    purpleRoomDark: "#3F2A5B",
    blueRoom: "#7DD3FC",
    blueRoomDark: "#0F3C63",
    portfolioRoom: "#FDA4A6",
    portfolioRoomDark: "#4F1F1F",
  },

  attendance: {
    [ATTENDANCE_STATUSES.ENROLLED]: "#747593",
    [ATTENDANCE_STATUSES.NOT_ENROLLED]: COLORS.TRANSPARENT,

    [ATTENDANCE_STATUSES.PRESENT]: "#8bd878",
    [ATTENDANCE_STATUSES.TARDY]: "#53a140",
    [ATTENDANCE_STATUSES.ABSENT]: "#dc4d4d",
    [ATTENDANCE_STATUSES.PLANNED_ABSENCE]: "#F18E00",

    [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: "#c6009e",
    [ATTENDANCE_STATUSES.TRYOUT_TARDY]: "#ad1a90",
    [ATTENDANCE_STATUSES.TRYOUT_ABSENT]: "#A800B6",
    [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: "#873376",

    [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: "#0dc0d1",
    [ATTENDANCE_STATUSES.MAKEUP_TARDY]: "#12a3bc",
    [ATTENDANCE_STATUSES.MAKEUP_ABSENT]: "#2656D3",
    [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: "#1275E3",

    [ATTENDANCE_STATUSES.INVALID]: "#fff",
  },

  colors: COLORS,
}

export const ThemeContext = createContext<ThemeContextType>(defaultValue)

const ThemeContextProvider = ({ children }: { children: ReactNode }) => {
  return (
    <ThemeContext.Provider value={defaultValue}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useThemeContext = () => useContext(ThemeContext)

export default ThemeContextProvider
